/**
 * V70: 휴가 첨부파일 업로드 Hook
 *
 * 공지사항 useAnnouncementUpload (useAnnouncements.ts:526-656) 패턴 클론.
 * 3단계: prepareLeaveUpload → PUT upload → confirmLeaveUpload
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

/** FE용 첨부파일 메타데이터 */
export interface LeaveAttachment {
  id: string;
  name: string;
  url: string;
  size: number;
  contentType: string;
  storagePath: string;
  uploadedAt: string;
}

interface PrepareUploadResponse {
  success: boolean;
  fileId: string;
  uploadUrl: string;
  storagePath: string;
  expiresIn: number;
}

interface ConfirmUploadResponse {
  success: boolean;
  downloadUrl: string;
  attachment: LeaveAttachment;
}

export interface UseLeaveUploadResult {
  /** 파일 업로드 실행 */
  uploadFile: (file: File) => Promise<LeaveAttachment>;
  /** 업로드 진행 중 여부 */
  isUploading: boolean;
  /** 에러 메시지 */
  error: string | null;
}

/**
 * 휴가 첨부파일 업로드 Hook
 */
export function useLeaveUpload(): UseLeaveUploadResult {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = useCallback(async (file: File): Promise<LeaveAttachment> => {
    if (!functions) throw new Error('Firebase가 초기화되지 않았습니다.');

    setIsUploading(true);
    setError(null);

    try {
      // 1. Signed URL 발급
      const prepareUpload = httpsCallable<
        { fileName: string; contentType: string; fileSize: number },
        PrepareUploadResponse
      >(functions, 'prepareLeaveUpload');

      const prepareResult = await prepareUpload({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
      });

      if (!prepareResult.data.success) {
        throw new Error('업로드 URL 발급 실패');
      }

      const { uploadUrl, storagePath, fileId } = prepareResult.data;

      // 2. Signed URL로 직접 업로드
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`파일 업로드 실패: ${uploadResponse.status}`);
      }

      // 3. 업로드 완료 확인
      const confirmUpload = httpsCallable<
        {
          fileId: string;
          storagePath: string;
          fileName: string;
          contentType: string;
          fileSize: number;
        },
        ConfirmUploadResponse
      >(functions, 'confirmLeaveUpload');

      const confirmResult = await confirmUpload({
        fileId,
        storagePath,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
      });

      if (!confirmResult.data.success) {
        throw new Error('업로드 확인 실패');
      }

      return confirmResult.data.attachment;
    } catch (err: any) {
      const msg = err?.message || '파일 업로드 실패';
      setError(msg);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, []);

  return { uploadFile, isUploading, error };
}
